import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { ChevronDown } from 'lucide-react';
import { Badge, cn, IconButton } from '@/components/ui';
import { computePointUncertainties } from '@/logic/adjustments';
import { hasResidual } from '@/logic/weights';
import type { PointPair, SystemPoint, TransformationSolution } from '@/types';
import type { FitSnapshot } from '@/types/adjustmentReview';
import { largestResidual } from './geometry';
import { LinkModeBar } from './LinkModeBar';
import { MapLegend } from './MapLegend';
import type {
  FitMovement,
  ResidualSegment,
  UncertaintyCircle,
} from './MapLayers';
import {
  transformedFitPoints,
  weightForShortcut,
  type Exaggeration,
} from './mapDisplay';
import {
  completePairs,
  datasetBounds,
  fitPanePoints,
  residualIndex,
  sourcePanePoints,
  unmatchedPoints,
  type MapPoint,
} from './mapModel';
import { MapPane, type PaneHover } from './MapPane';
import { MapToolbar } from './MapToolbar';
import { PointContextMenu, type PointMenuState } from './PointContextMenu';
import { PointHoverCard } from './PointHoverCard';
import { SelectedPairCard } from './SelectedPairCard';
import { useMapViewport } from './useMapViewport';

export interface InteractiveMapProps {
  units?: string;
  pointPairs: PointPair[];
  system1Points?: SystemPoint[];
  system2Points?: SystemPoint[];
  solution?: TransformationSolution | null;
  solutionStale?: boolean;
  selectedPointId: number | null;
  onSelectPoint: (id: number | null) => void;
  onSetWeight: (id: number, weight: number) => void;
  onLinkPoints?: (sys1Point: string, sys2Point: string) => void;
  onRemovePair?: (id: number) => void;
  onEditPair?: (id: number) => void;
  defaultExpanded?: boolean;
  compact?: boolean;
  previousFit?: FitSnapshot | null;
  trialSolution?: TransformationSolution | null;
  flaggedPointIds?: readonly number[];
}

type ViewMode = 'side-by-side' | 'overlay';
type PaneId = 'record' | 'field' | 'fit';
const NO_FLAGS: readonly number[] = [];

const isEditableTarget = (target: EventTarget) =>
  target instanceof HTMLElement &&
  !!target.closest('input,textarea,select,[contenteditable="true"]');

const PaneTitle = ({
  system,
  children,
}: {
  system: 1 | 2;
  children: string;
}) => (
  <>
    <span
      aria-hidden="true"
      className={cn(
        'size-2 rounded-full',
        system === 1 ? 'bg-record' : 'bg-field'
      )}
    />
    {children}
  </>
);

/**
 * Interactive map of Record and Field points: the fit overlay (observed Field
 * with transformed Record and residual vectors) or the two datasets side by
 * side, with selection, weighting, and point linking.
 *
 * Framing, zoom, and the automatic residual exaggeration stay stable across
 * edits, trials, and solves; replacing a source dataset resets the view.
 */
export const InteractiveMap = ({
  units,
  pointPairs,
  system1Points,
  system2Points,
  solution: currentSolution,
  solutionStale,
  selectedPointId,
  onSelectPoint,
  onSetWeight,
  onLinkPoints,
  onRemovePair,
  onEditPair,
  defaultExpanded = false,
  compact = false,
  previousFit,
  trialSolution,
  flaggedPointIds = NO_FLAGS,
}: InteractiveMapProps) => {
  const solution = trialSolution ?? currentSolution;
  const fit = solution && !solution.error ? solution : null;
  const isTrial = !!trialSolution;
  const legendId = `${useId().replace(/:/g, '')}-legend`;
  const flaggedIds = useMemo(() => new Set(flaggedPointIds), [flaggedPointIds]);

  // ---- View and tool state -------------------------------------------------
  const [expanded, setExpanded] = useState(defaultExpanded);
  useEffect(() => {
    if (selectedPointId !== null) setExpanded(true);
  }, [selectedPointId]);
  const [viewMode, setViewMode] = useState<ViewMode>(
    fit ? 'overlay' : 'side-by-side'
  );
  // Show the fit when one first appears or a trial starts; later re-solves
  // keep the user's chosen view.
  const hasFit = !!fit;
  useEffect(() => {
    if (hasFit || isTrial) setViewMode('overlay');
  }, [hasFit, isTrial]);
  const [compareFit, setCompareFit] = useState(false);
  const [showResiduals, setShowResiduals] = useState(true);
  const [exaggeration, setExaggeration] = useState<Exaggeration>('auto');
  const [showUncertainty, setShowUncertainty] = useState(false);
  const [legendOpen, setLegendOpen] = useState(false);
  const [linkMode, setLinkMode] = useState(false);
  const [linkSource, setLinkSource] = useState<string | null>(null);
  const [linkedPairs, setLinkedPairs] = useState<
    Array<{ sys1: string; sys2: string }>
  >([]);
  const [menu, setMenu] = useState<PointMenuState | null>(null);
  const [hover, setHover] = useState<(PaneHover & { pane: PaneId }) | null>(
    null
  );
  const recordViewport = useMapViewport();
  const fieldViewport = useMapViewport();

  // ---- Data ----------------------------------------------------------------
  const validPairs = useMemo(() => completePairs(pointPairs), [pointPairs]);
  const pairById = useMemo(
    () => new Map(pointPairs.map((pair) => [pair.id, pair])),
    [pointPairs]
  );
  const unmatched1 = useMemo(
    () => unmatchedPoints(system1Points, pointPairs, 1),
    [system1Points, pointPairs]
  );
  const unmatched2 = useMemo(
    () => unmatchedPoints(system2Points, pointPairs, 2),
    [system2Points, pointPairs]
  );
  const bounds = useMemo(
    () => datasetBounds(validPairs, unmatched1, unmatched2),
    [validPairs, unmatched1, unmatched2]
  );
  const residuals = useMemo(() => residualIndex(fit), [fit]);

  // ---- Stable framing --------------------------------------------------------
  // The frame stays fixed through edits, solves, and trials; "Zoom to fit"
  // adopts the current bounds. Replacing a source dataset resets the view.
  const [frameBounds, setFrameBounds] = useState(bounds);
  const [source, setSource] = useState({ system1Points, system2Points });
  const [sourceVersion, setSourceVersion] = useState(0);
  if (
    source.system1Points !== system1Points ||
    source.system2Points !== system2Points
  ) {
    setSource({ system1Points, system2Points });
    setSourceVersion((version) => version + 1);
    setFrameBounds(bounds);
    setCompareFit(false);
    setViewMode(fit ? 'overlay' : 'side-by-side');
    setLinkMode(false);
    setLinkSource(null);
    setLinkedPairs([]);
    setMenu(null);
    setHover(null);
  } else if (!bounds) {
    if (frameBounds) setFrameBounds(null);
  } else if (
    !frameBounds ||
    (!frameBounds.sys1 && bounds.sys1) ||
    (!frameBounds.sys2 && bounds.sys2)
  ) {
    setFrameBounds({
      sys1: frameBounds?.sys1 ?? bounds.sys1,
      sys2: frameBounds?.sys2 ?? bounds.sys2,
    });
  }
  const { reset: resetRecordView } = recordViewport;
  const { reset: resetFieldView } = fieldViewport;
  useLayoutEffect(() => {
    if (sourceVersion === 0) return;
    resetRecordView();
    resetFieldView();
  }, [sourceVersion, resetRecordView, resetFieldView]);

  // Freeze the automatic vector reference at the first displayed fit, so a
  // smaller trial residual is not drawn as large as the original one.
  // The reference is cleared without a fit and when the source is replaced.
  const [autoReference, setAutoReference] = useState<number | null>(null);
  const [referenceVersion, setReferenceVersion] = useState(sourceVersion);
  if (referenceVersion !== sourceVersion) {
    setReferenceVersion(sourceVersion);
    setAutoReference(null);
  } else if (!fit && autoReference !== null) {
    setAutoReference(null);
  } else if (fit && autoReference === null) {
    setAutoReference(largestResidual(fit.residuals));
  }
  const residualReference =
    autoReference ?? (fit ? largestResidual(fit.residuals) : 1);

  // ---- Fit geometry ----------------------------------------------------------
  const transformed = useMemo(
    () =>
      fit
        ? transformedFitPoints({
            pointPairs: validPairs.filter((pair) => residuals.has(pair.id)),
            solution: fit,
          })
        : [],
    [fit, validPairs, residuals]
  );
  const transformedById = useMemo(
    () => new Map(transformed.map((point) => [point.id, point])),
    [transformed]
  );

  const fitResidualSegments = useMemo<ResidualSegment[]>(
    () =>
      validPairs.flatMap((pair) => {
        const residual = residuals.get(pair.id);
        const start = transformedById.get(pair.id);
        if (!start || !residual || residual.total === null) return [];
        if (!hasResidual(pair.weight)) return [];
        return [
          {
            id: pair.id,
            from: { n: start.n, e: start.e },
            to: { n: parseFloat(pair.n2), e: parseFloat(pair.e2) },
          },
        ];
      }),
    [validPairs, residuals, transformedById]
  );
  const fieldResidualSegments = useMemo<ResidualSegment[]>(
    () =>
      validPairs.flatMap((pair) => {
        const residual = residuals.get(pair.id);
        if (!residual || residual.vn === null || residual.ve === null) {
          return [];
        }
        if (!hasResidual(pair.weight)) return [];
        const n = parseFloat(pair.n2);
        const e = parseFloat(pair.e2);
        return [
          {
            id: pair.id,
            from: { n: n - residual.vn, e: e - residual.ve },
            to: { n, e },
          },
        ];
      }),
    [validPairs, residuals]
  );

  const uncertaintySupported =
    !!fit && !fit.constrained && !fit.minimallyConstrained;
  const uncertainties = useMemo(
    () =>
      uncertaintySupported && fit
        ? computePointUncertainties(pointPairs, fit)
        : null,
    [uncertaintySupported, fit, pointPairs]
  );
  const uncertaintyCircles = useMemo<UncertaintyCircle[] | null>(
    () =>
      showUncertainty && uncertainties
        ? uncertainties.flatMap((uncertainty) => {
            const center = transformedById.get(uncertainty.id);
            return center
              ? [
                  {
                    id: uncertainty.id,
                    center: { n: center.n, e: center.e },
                    radius: uncertainty.semiMajor,
                  },
                ]
              : [];
          })
        : null,
    [showUncertainty, uncertainties, transformedById]
  );

  const canCompare = !!previousFit && !previousFit.solution.error && !!fit;
  const comparing = compareFit && canCompare;
  const referenceLabel = isTrial ? 'Current' : 'Previous';
  const movements = useMemo<FitMovement[] | null>(() => {
    if (!comparing || !previousFit) return null;
    const currentByPoint = new Map(
      transformed.map((point) => [point.point, point])
    );
    return transformedFitPoints(previousFit).map((previous) => {
      const current = currentByPoint.get(previous.point);
      return {
        id: previous.id,
        point: previous.point,
        previous: { n: previous.n, e: previous.e },
        current: current ? { n: current.n, e: current.e } : null,
      };
    });
  }, [comparing, previousFit, transformed]);

  // ---- Pane points ----------------------------------------------------------
  const recordPoints = useMemo(
    () => sourcePanePoints(1, validPairs, unmatched1, flaggedIds),
    [validPairs, unmatched1, flaggedIds]
  );
  const fieldPoints = useMemo(
    () => sourcePanePoints(2, validPairs, unmatched2, flaggedIds),
    [validPairs, unmatched2, flaggedIds]
  );
  const fitPoints = useMemo(
    () =>
      fitPanePoints(
        validPairs,
        transformed,
        unmatched1,
        unmatched2,
        fit,
        flaggedIds
      ),
    [validPairs, transformed, unmatched1, unmatched2, fit, flaggedIds]
  );
  const linkSourceKey = useMemo(
    () =>
      linkMode && linkSource !== null
        ? (recordPoints.find((point) => point.label === linkSource)?.key ??
          null)
        : null,
    [linkMode, linkSource, recordPoints]
  );

  // ---- Actions ----------------------------------------------------------------
  const startLinking = useCallback((sourceLabel: string | null) => {
    setLinkMode(true);
    setLinkSource(sourceLabel);
    setExpanded(true);
    setViewMode('side-by-side');
    setMenu(null);
  }, []);
  const stopLinking = useCallback(() => {
    setLinkMode(false);
    setLinkSource(null);
  }, []);

  const handleActivate = useCallback(
    (pane: PaneId, point: MapPoint) => {
      const linking =
        onLinkPoints &&
        (point.kind === 'unmatched' || (linkMode && pane !== 'fit'));
      if (linking) {
        if (!linkMode) {
          startLinking(point.system === 1 ? point.label : null);
        } else if (point.system === 1) {
          setLinkSource(point.label);
        } else if (linkSource !== null) {
          onLinkPoints(linkSource, point.label);
          setLinkedPairs((previous) => [
            ...previous,
            { sys1: linkSource, sys2: point.label },
          ]);
          setLinkSource(null);
        }
        return;
      }
      if (point.pairId === null) return;
      onSelectPoint(selectedPointId === point.pairId ? null : point.pairId);
    },
    [
      linkMode,
      linkSource,
      onLinkPoints,
      onSelectPoint,
      selectedPointId,
      startLinking,
    ]
  );
  const activateRecord = useCallback(
    (point: MapPoint) => handleActivate('record', point),
    [handleActivate]
  );
  const activateField = useCallback(
    (point: MapPoint) => handleActivate('field', point),
    [handleActivate]
  );
  const activateFit = useCallback(
    (point: MapPoint) => handleActivate('fit', point),
    [handleActivate]
  );

  const handleUndoLink = useCallback(() => {
    const last = linkedPairs[linkedPairs.length - 1];
    if (!last || !onRemovePair) return;
    const pair = pointPairs.find(
      (p) => p.sys1Point === last.sys1 && p.sys2Point === last.sys2
    );
    if (pair) onRemovePair(pair.id);
    setLinkedPairs((previous) => previous.slice(0, -1));
  }, [linkedPairs, onRemovePair, pointPairs]);

  const openMenu = useCallback(
    (point: MapPoint, position: { x: number; y: number }, element: Element) => {
      if (point.pairId === null) return;
      setHover(null);
      setMenu({ pairId: point.pairId, ...position, returnFocus: element });
      onSelectPoint(point.pairId);
    },
    [onSelectPoint]
  );
  const closeMenu = useCallback(
    () => setMenu((current) => (current === null ? current : null)),
    []
  );

  const handleBackgroundClick = useCallback(() => {
    if (selectedPointId !== null) onSelectPoint(null);
  }, [onSelectPoint, selectedPointId]);

  const hoverRecord = useCallback(
    (next: PaneHover | null) => setHover(next && { ...next, pane: 'record' }),
    []
  );
  const hoverField = useCallback(
    (next: PaneHover | null) => setHover(next && { ...next, pane: 'field' }),
    []
  );
  const hoverFit = useCallback(
    (next: PaneHover | null) => setHover(next && { ...next, pane: 'fit' }),
    []
  );

  const fitRecordFrame = useCallback(() => {
    setFrameBounds((frame) => ({
      sys1: bounds?.sys1 ?? null,
      sys2: frame?.sys2 ?? bounds?.sys2 ?? null,
    }));
    resetRecordView();
  }, [bounds, resetRecordView]);
  const fitFieldFrame = useCallback(() => {
    setFrameBounds((frame) => ({
      sys1: frame?.sys1 ?? bounds?.sys1 ?? null,
      sys2: bounds?.sys2 ?? null,
    }));
    resetFieldView();
  }, [bounds, resetFieldView]);

  // Shortcuts apply only while focus is in the map and outside editable controls.
  const handleMapKeyDown = (event: ReactKeyboardEvent) => {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (isEditableTarget(event.target)) return;
    if (event.key === 'Escape') {
      if (linkSource !== null) setLinkSource(null);
      else if (linkMode) setLinkMode(false);
      else if (selectedPointId !== null) onSelectPoint(null);
      else return;
      event.preventDefault();
      return;
    }
    if (selectedPointId === null) return;
    const weight = weightForShortcut(event.key);
    if (weight === undefined) return;
    event.preventDefault();
    onSetWeight(selectedPointId, weight);
  };

  // ---- Toolbar models (memoized so pan/zoom frames skip re-rendering them) ---
  const toggleResiduals = useCallback(() => setShowResiduals((v) => !v), []);
  const toggleUncertainty = useCallback(
    () => setShowUncertainty((v) => !v),
    []
  );
  const toggleCompare = useCallback(() => {
    setCompareFit((v) => !v);
    setViewMode('overlay');
    stopLinking();
  }, [stopLinking]);
  const toggleLink = useCallback(() => {
    if (linkMode) stopLinking();
    else startLinking(null);
  }, [linkMode, startLinking, stopLinking]);
  const toggleSideBySide = useCallback(() => {
    setViewMode((mode) =>
      mode === 'side-by-side' ? 'overlay' : 'side-by-side'
    );
    stopLinking();
  }, [stopLinking]);
  const toggleLegend = useCallback(() => setLegendOpen((v) => !v), []);
  const closeLegend = useCallback(() => setLegendOpen(false), []);

  const vectorsShown = showResiduals || (showUncertainty && !!uncertainties);
  const residualTool = useMemo(
    () =>
      fit
        ? {
            shown: showResiduals,
            onToggle: toggleResiduals,
            exaggeration,
            exaggerationEnabled: vectorsShown,
            onExaggerationChange: setExaggeration,
          }
        : null,
    [fit, showResiduals, toggleResiduals, exaggeration, vectorsShown]
  );
  const vectorScale = useMemo(
    () => (fit ? { exaggeration, reference: residualReference } : null),
    [fit, exaggeration, residualReference]
  );
  const uncertaintyTool = useMemo(
    () =>
      uncertaintySupported
        ? {
            shown: showUncertainty && !!uncertainties,
            onToggle: toggleUncertainty,
            unavailableReason: uncertainties
              ? undefined
              : 'Needs redundancy (three or more active pairs)',
          }
        : null,
    [uncertaintySupported, showUncertainty, uncertainties, toggleUncertainty]
  );
  const compareTool = useMemo(
    () =>
      canCompare
        ? {
            label: isTrial ? 'Compare current fit' : 'Compare previous fit',
            pressed: comparing,
            onToggle: toggleCompare,
          }
        : null,
    [canCompare, isTrial, comparing, toggleCompare]
  );
  const linkTool = useMemo(
    () => (onLinkPoints ? { pressed: linkMode, onToggle: toggleLink } : null),
    [onLinkPoints, linkMode, toggleLink]
  );
  const sideBySide = viewMode === 'side-by-side';
  const sideBySideTool = useMemo(
    () => ({ pressed: sideBySide, onToggle: toggleSideBySide }),
    [sideBySide, toggleSideBySide]
  );
  const legendTool = useMemo(
    () => ({ open: legendOpen, onToggle: toggleLegend, controls: legendId }),
    [legendOpen, toggleLegend, legendId]
  );

  if (
    validPairs.length === 0 &&
    unmatched1.length === 0 &&
    unmatched2.length === 0
  ) {
    return null;
  }

  const selectedPair =
    selectedPointId !== null ? pairById.get(selectedPointId) : undefined;
  const hoveredPair =
    hover?.point.pairId != null ? pairById.get(hover.point.pairId) : undefined;
  const menuPair = menu ? pairById.get(menu.pairId) : undefined;
  const drawResiduals = !!fit && showResiduals;
  const sharedPaneProps = {
    selectedPairId: selectedPointId,
    units,
    defaultWidth: compact || sideBySide ? 450 : 900,
    onOpenMenu: openMenu,
    onBackgroundClick: handleBackgroundClick,
  };

  return (
    <section
      role="region"
      aria-label="Coordinate visualization"
      onKeyDown={handleMapKeyDown}
      className="rounded-xl border border-border bg-surface shadow-sm"
    >
      <div
        className={cn(
          'flex flex-wrap items-center gap-2',
          compact ? 'px-3 pt-3' : 'px-4 pt-4 sm:px-5'
        )}
      >
        <div className="mr-auto flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <h3 className="text-sm font-semibold text-fg">Map</h3>
          {isTrial && (
            <Badge tone="accent" dot>
              Trial preview
            </Badge>
          )}
          {fit && solutionStale && !isTrial && (
            <Badge
              tone="warning"
              dot
              title="Point data changed since this fit; re-solve to update it"
            >
              Outdated fit
            </Badge>
          )}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {expanded && (
            <MapToolbar
              residuals={residualTool}
              uncertainty={uncertaintyTool}
              compare={compareTool}
              link={linkTool}
              sideBySide={sideBySideTool}
              legend={legendTool}
            />
          )}
          <IconButton
            aria-label={
              expanded ? 'Collapse coordinate map' : 'Expand coordinate map'
            }
            tooltip={expanded ? 'Collapse map' : 'Expand map'}
            aria-expanded={expanded}
            size="sm"
            className="-mr-1"
            onClick={() => setExpanded((v) => !v)}
            icon={
              <ChevronDown
                className={cn(
                  'transition-transform duration-200',
                  expanded && 'rotate-180'
                )}
              />
            }
          />
        </div>
      </div>

      {expanded && (
        <div
          className={cn(
            'space-y-2.5',
            compact ? 'px-3 pb-3 pt-2.5' : 'px-4 pb-4 pt-3 sm:px-5'
          )}
        >
          {linkMode && (
            <LinkModeBar
              source={linkSource}
              linkedCount={linkedPairs.length}
              onUndo={onRemovePair ? handleUndoLink : undefined}
              onDone={stopLinking}
            />
          )}

          <div className="relative">
            <div
              className={cn(
                'grid gap-2.5',
                sideBySide && !compact && 'md:grid-cols-2'
              )}
            >
              {sideBySide ? (
                <>
                  <MapPane
                    {...sharedPaneProps}
                    name="Record map"
                    label={<PaneTitle system={1}>Record</PaneTitle>}
                    bounds={frameBounds?.sys1 ?? null}
                    viewport={recordViewport}
                    onFit={fitRecordFrame}
                    points={recordPoints}
                    hoveredKey={
                      hover?.pane === 'record' ? hover.point.key : null
                    }
                    linkSourceKey={linkSourceKey}
                    emptyMessage="No Record points"
                    onActivate={activateRecord}
                    onHover={hoverRecord}
                  />
                  <MapPane
                    {...sharedPaneProps}
                    name="Field map"
                    label={<PaneTitle system={2}>Field</PaneTitle>}
                    bounds={frameBounds?.sys2 ?? null}
                    viewport={fieldViewport}
                    onFit={fitFieldFrame}
                    points={fieldPoints}
                    hoveredKey={
                      hover?.pane === 'field' ? hover.point.key : null
                    }
                    linkSourceKey={null}
                    emptyMessage="No Field points"
                    vectorScale={vectorScale}
                    residuals={drawResiduals ? fieldResidualSegments : null}
                    uncertainty={uncertaintyCircles}
                    onActivate={activateField}
                    onHover={hoverField}
                  />
                </>
              ) : (
                <MapPane
                  {...sharedPaneProps}
                  name="Fit map"
                  label={
                    fit ? (
                      <>
                        <span className={isTrial ? 'text-accent' : undefined}>
                          {isTrial ? 'Trial fit' : 'Fit'}
                        </span>
                        <span className="hidden font-normal text-fg-muted sm:inline">
                          Field with transformed Record
                        </span>
                      </>
                    ) : (
                      <PaneTitle system={2}>Field</PaneTitle>
                    )
                  }
                  bounds={frameBounds?.sys2 ?? null}
                  viewport={fieldViewport}
                  onFit={fitFieldFrame}
                  points={fitPoints}
                  hoveredKey={hover?.pane === 'fit' ? hover.point.key : null}
                  linkSourceKey={null}
                  vectorScale={vectorScale}
                  residuals={drawResiduals ? fitResidualSegments : null}
                  uncertainty={uncertaintyCircles}
                  comparison={movements ? { movements, referenceLabel } : null}
                  onActivate={activateFit}
                  onHover={hoverFit}
                />
              )}
            </div>
            <MapLegend
              id={legendId}
              open={legendOpen}
              onClose={closeLegend}
              mode={sideBySide ? 'source' : 'fit'}
              hasFit={!!fit}
              residualsShown={drawResiduals}
              compareLabel={movements ? referenceLabel : null}
              uncertaintyShown={!!uncertaintyCircles}
              hasUnmatched={unmatched1.length + unmatched2.length > 0}
              hasFlagged={flaggedIds.size > 0}
              className="absolute left-2 top-11 z-10 max-h-[calc(100%-3.25rem)] overflow-y-auto"
            />
          </div>

          {selectedPair && (
            <SelectedPairCard
              pair={selectedPair}
              residual={residuals.get(selectedPair.id)}
              flagged={flaggedIds.has(selectedPair.id)}
              units={units}
              onSetWeight={onSetWeight}
              onEditPair={onEditPair}
              onRemovePair={onRemovePair}
            />
          )}

          <p className="text-xs text-fg-muted">
            {linkMode ? (
              'Choose a Record point, then its Field point · Esc to cancel'
            ) : (
              <>
                <span className="[@media(pointer:coarse)]:hidden">
                  Click to select · Right-click for options · Scroll to zoom ·
                  Drag to pan
                </span>
                <span className="hidden [@media(pointer:coarse)]:inline">
                  Tap to select · Drag to pan · Pinch to zoom
                </span>
              </>
            )}
          </p>
        </div>
      )}

      {menu && menuPair && (
        <PointContextMenu
          state={menu}
          pair={menuPair}
          onClose={closeMenu}
          onSetWeight={onSetWeight}
          onEditPair={onEditPair}
          onRemovePair={onRemovePair}
        />
      )}
      {hover && !menu && (
        <PointHoverCard
          point={hover.point}
          x={hover.x}
          y={hover.y}
          pair={hoveredPair}
          residual={hoveredPair ? residuals.get(hoveredPair.id) : undefined}
          flagged={hoveredPair ? flaggedIds.has(hoveredPair.id) : false}
          canLink={!!onLinkPoints}
          units={units}
        />
      )}
    </section>
  );
};
